import { useNavigate } from "react-router-dom";
import { EuiFlexGroup, EuiFlexItem, EuiToolTip } from "@elastic/eui";
import { Profile } from "../profileTypes";
import LinkToList from "components/Popover/LinkToList";
import { AlertIcon, ResourcesIcon } from "components/Icons";
import NotificationChannelLinkToList from "./NotificationChannelLinkToList";
import { REDIRECT_TO_INVENTORY_INSTANCES } from "settings/menu/infrastructurePages";
import { REDIRECT_TO_ALERT_TYPES } from "settings/menu/admin_console";
import { REDIRECT_TO_RESOURCES } from "settings/menu/admin_console";
import { generateExcelFileName } from "utils";

const ProfileAssignmentIcons = ({ profile }: { profile: Profile }) => {
  const navigate = useNavigate();

  const resourceColumns = [
    {
      field: "resourceName",
      name: "Resource Name",
    },
    {
      field: "resourceType",
      name: "Resource Type",
    },
    {
      field: "platformName",
      name: "Platform",
    },
  ];

  const alertTypeColumns = [
    {
      field: "name",
      name: "Name",
    },
    {
      field: "severityTypeName",
      name: "Severity",
      width: 150,
      render: (severity: any) => {
        return (
          <div>
            <strong>Severity: </strong>
            <span>{severity}</span>
          </div>
        );
      },
    },
    {
      field: "toleranceTypeName",
      name: "Tolerance",
      width: 150,
      render: (tolerance: any) => {
        return (
          <div>
            <strong>Tolerance: </strong>
            <span>{tolerance}</span>
          </div>
        );
      },
    },
  ];

  const hasInstanceAssignments = profile.resourceAssignments.length > 0;

  // Filter to unique alert types, preferring level 1 when multiple levels exist
  const uniqueAlertTypeAssignments = profile.alertTypeAssignments.reduce(
    (acc, alert) => {
      const key = `${alert.alertTypeId}-${alert.alertSubTypeId ?? "null"}`;
      const existing = acc.find(
        (item) => `${item.alertTypeId}-${item.alertSubTypeId ?? "null"}` === key
      );

      if (!existing) {
        acc.push(alert);
      } else if (alert.alertLevel === 1 && existing.alertLevel !== 1) {
        const index = acc.indexOf(existing);
        acc[index] = alert;
      }

      return acc;
    },
    [] as typeof profile.alertTypeAssignments
  );

  const hasAlertTypeAssignments = uniqueAlertTypeAssignments.length > 0;

  return (
    <EuiFlexGroup
      responsive={false}
      style={{ width: "fit-content" }}
      alignItems="center"
      gutterSize="m"
      justifyContent="center">
      <EuiFlexItem
        grow={false}
        style={{ marginBottom: "-7px", minWidth: "65px" }}
        onClick={(e: any) => e.stopPropagation()}>
        <LinkToList
          Columns={resourceColumns}
          Items={profile.resourceAssignments}
          PopOverMaxWidth={450}
          showExport={true}
          exportFileName={generateExcelFileName(`Profile Resources ${profile.name}`)}
          showHeaders={true}
          ButtonTitle={
            <EuiToolTip content="Assigned resources">
              <EuiFlexGroup alignItems="center" gutterSize="s">
                <EuiFlexItem>
                  <ResourcesIcon fill={hasInstanceAssignments ? "#FF7E62" : "var(--text)"} />
                </EuiFlexItem>
                <EuiFlexItem
                  style={{
                    color: "var(--text)",
                  }}>
                  {profile.resourceAssignments.length}
                </EuiFlexItem>
              </EuiFlexGroup>
            </EuiToolTip>
          }
          belowButton={{
            name: "View Resources",
            onClick: () => {
              navigate(REDIRECT_TO_RESOURCES);
            },
          }}
        />
      </EuiFlexItem>

      <EuiFlexItem
        grow={false}
        style={{ marginBottom: "-7px", minWidth: "65px" }}
        onClick={(e: any) => e.stopPropagation()}>
        <LinkToList
          Copy={false}
          Columns={alertTypeColumns}
          Items={uniqueAlertTypeAssignments}
          PopOverMaxWidth={600}
          showExport={true}
          exportFileName={generateExcelFileName(`Profile Alert Types ${profile.name}`)}
          showHeaders={true}
          ButtonTitle={
            <EuiToolTip content="Assigned alert types">
              <EuiFlexGroup alignItems="center" gutterSize="s">
                <EuiFlexItem>
                  <AlertIcon fill={hasAlertTypeAssignments ? "var(--amber)" : "var(--text)"} />
                </EuiFlexItem>
                <EuiFlexItem
                  style={{
                    color: "var(--text)",
                  }}>
                  {uniqueAlertTypeAssignments.length}
                </EuiFlexItem>
              </EuiFlexGroup>
            </EuiToolTip>
          }
          belowButton={{
            name: "View Alerts Types",
            onClick: () => {
              navigate(REDIRECT_TO_ALERT_TYPES);
            },
          }}
        />
      </EuiFlexItem>

      <EuiFlexItem
        grow={false}
        style={{ marginBottom: "-7px", minWidth: "65px" }}
        onClick={(e: any) => e.stopPropagation()}>
        <NotificationChannelLinkToList profile={profile} />
      </EuiFlexItem>
    </EuiFlexGroup>
  );
};

export default ProfileAssignmentIcons;
